<script lang="ts">
  import type { DecodedImage } from '$lib/image/decode'
  import { normalizeCrop } from '$lib/image/resize'
  import type { CropRect } from '$lib/types'

  interface Props {
    image: DecodedImage
    name: string
    /** Current selection in source pixels, or null for the whole image. */
    crop: CropRect | null
    oncrop: (crop: CropRect | null) => void
  }

  const { image, name, crop, oncrop }: Props = $props()

  let canvas = $state<HTMLCanvasElement | null>(null)
  let surface = $state<HTMLDivElement | null>(null)
  /** Live rectangle while dragging, in source pixels; null when idle. */
  let drag = $state<CropRect | null>(null)
  let origin: { x: number; y: number } | null = null

  $effect(() => {
    const target = canvas
    if (!target) return
    target.width = image.width
    target.height = image.height
    const ctx = target.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, image.width, image.height)
    ctx.drawImage(image.bitmap, 0, 0)
  })

  const selection = $derived(drag ?? crop)

  /** Selection as CSS percentages, so it tracks the element's own size. */
  const box = $derived(
    selection
      ? {
          left: `${(selection.x / image.width) * 100}%`,
          top: `${(selection.y / image.height) * 100}%`,
          width: `${(selection.width / image.width) * 100}%`,
          height: `${(selection.height / image.height) * 100}%`,
        }
      : null,
  )

  /** Pointer position in source-image pixels. */
  function toImage(event: PointerEvent): { x: number; y: number } | null {
    const element = surface
    if (!element) return null
    const rect = element.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return null
    return {
      x: ((event.clientX - rect.left) / rect.width) * image.width,
      y: ((event.clientY - rect.top) / rect.height) * image.height,
    }
  }

  function rectBetween(a: { x: number; y: number }, b: { x: number; y: number }): CropRect {
    return {
      x: Math.min(a.x, b.x),
      y: Math.min(a.y, b.y),
      width: Math.abs(a.x - b.x),
      height: Math.abs(a.y - b.y),
    }
  }

  function onPointerDown(event: PointerEvent) {
    if (event.button !== 0) return
    const point = toImage(event)
    if (!point) return
    event.preventDefault()
    origin = point
    drag = rectBetween(point, point)
    ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
  }

  function onPointerMove(event: PointerEvent) {
    if (!origin) return
    const point = toImage(event)
    if (point) drag = rectBetween(origin, point)
  }

  function onPointerUp(event: PointerEvent) {
    if (!origin) return
    const element = event.currentTarget as HTMLElement
    if (element.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId)
    const pending = drag
    origin = null
    drag = null
    // A click without a drag clears the selection rather than creating a
    // one-pixel crop nobody asked for.
    oncrop(normalizeCrop(pending, image.width, image.height))
  }

  function clear() {
    oncrop(null)
  }
</script>

<figure class="crop" data-testid="image-preview">
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="surface"
    bind:this={surface}
    onpointerdown={onPointerDown}
    onpointermove={onPointerMove}
    onpointerup={onPointerUp}
    onpointercancel={onPointerUp}
    data-testid="crop-surface"
  >
    <canvas bind:this={canvas} data-testid="preview-canvas"></canvas>

    {#if box}
      <!-- Four shades rather than one box-shadow: a shadow would also darken
           anything overlapping the figure, and this stays inside the image. -->
      <div class="shade" style:left="0" style:top="0" style:right="0" style:height={box.top}></div>
      <div class="shade" style:left="0" style:top={box.top} style:width={box.left} style:height={box.height}></div>
      <div
        class="shade"
        style:top={box.top}
        style:left="calc({box.left} + {box.width})"
        style:right="0"
        style:height={box.height}
      ></div>
      <div class="shade" style:left="0" style:top="calc({box.top} + {box.height})" style:right="0" style:bottom="0"></div>

      <div
        class="marquee"
        style:left={box.left}
        style:top={box.top}
        style:width={box.width}
        style:height={box.height}
        data-testid="crop-marquee"
      ></div>
    {/if}
  </div>

  <figcaption>
    <span class="name">{name}</span>
    {#if selection}
      <span class="dims" data-testid="crop-dimensions">
        crop {Math.round(selection.width)} × {Math.round(selection.height)}
      </span>
      <button type="button" onclick={clear} data-testid="crop-clear">Clear crop</button>
    {:else}
      <span class="dims" data-testid="preview-dimensions">{image.width} × {image.height}</span>
      <span class="hint">drag to convert part of the image</span>
    {/if}
  </figcaption>
</figure>

<style>
  .crop {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .surface {
    position: relative;
    line-height: 0;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    cursor: crosshair;
    touch-action: none;
  }

  canvas {
    display: block;
    width: 100%;
    height: auto;
    max-height: 70vh;
    object-fit: contain;
  }

  .shade {
    position: absolute;
    background: color-mix(in srgb, var(--bg) 55%, transparent);
    pointer-events: none;
  }

  .marquee {
    position: absolute;
    border: 1px solid var(--accent);
    box-shadow: 0 0 0 1px color-mix(in srgb, #fff 60%, transparent) inset;
    pointer-events: none;
  }

  figcaption {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.8rem;
    color: var(--text-muted);
    line-height: 1.5;
  }

  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dims {
    font-family: var(--mono);
    flex-shrink: 0;
    margin-left: auto;
  }

  .hint {
    flex-shrink: 0;
  }

  button {
    padding: 0.2rem 0.55rem;
    background: var(--surface-raised);
    border: 1px solid var(--border-strong);
    border-radius: 6px;
    font-size: 0.8rem;
  }
</style>
